import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Users, BedDouble, Calendar, TrendingUp } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const { selectedProperty } = useOutletContext();
  const { t } = useTranslation();
  const [arrivals, setArrivals] = useState([]);
  const [departures, setDepartures] = useState([]);
  const [occupancy, setOccupancy] = useState(null);
  const [inHouse, setInHouse] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (selectedProperty) {
      fetchDashboardData();
    }
  }, [selectedProperty]);

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const today = new Date().toISOString().split('T')[0];

      const [arrivalsRes, departuresRes, occupancyRes, inHouseRes] = await Promise.all([
        axios.get(`${API}/reports/arrivals?property_id=${selectedProperty.id}&date=${today}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/reports/departures?property_id=${selectedProperty.id}&date=${today}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/reports/occupancy?property_id=${selectedProperty.id}&date=${today}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/reservations?property_id=${selectedProperty.id}&status=IN_HOUSE`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      setArrivals(arrivalsRes.data);
      setDepartures(departuresRes.data);
      setOccupancy(occupancyRes.data);
      setInHouse(inHouseRes.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!selectedProperty) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <p className="text-slate-600 mb-4">{t('common.selectProperty')}</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return <div className="flex items-center justify-center h-full">{t('common.loading')}</div>;
  }

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div>
        <h1 className="text-3xl font-bold text-slate-900">{t('nav.dashboard')}</h1>
        <p className="text-slate-600 mt-1">Overview of {selectedProperty.name}</p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="border-l-4 border-l-cyan-500" data-testid="arrivals-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-slate-600 flex items-center gap-2">
              <Calendar size={18} />
              Today's Arrivals
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-slate-900">{arrivals.length}</div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-blue-500" data-testid="departures-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-slate-600 flex items-center gap-2">
              <Calendar size={18} />
              Today's Departures
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-slate-900">{departures.length}</div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-emerald-500" data-testid="inhouse-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-slate-600 flex items-center gap-2">
              <Users size={18} />
              In-House Guests
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-slate-900">{inHouse.length}</div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-amber-500" data-testid="occupancy-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-slate-600 flex items-center gap-2">
              <TrendingUp size={18} />
              Occupancy Rate
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-slate-900">
              {occupancy ? `${occupancy.occupancy_rate}%` : '0%'}
            </div>
            {occupancy && (
              <p className="text-sm text-slate-500 mt-1">
                {occupancy.occupied_units} / {occupancy.total_units} rooms
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Today's Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card data-testid="arrivals-list">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-cyan-100 flex items-center justify-center">
                <Calendar size={18} className="text-cyan-600" />
              </div>
              Today's Arrivals
            </CardTitle>
          </CardHeader>
          <CardContent>
            {arrivals.length === 0 ? (
              <p className="text-slate-500 text-center py-8">No arrivals today</p>
            ) : (
              <div className="space-y-3">
                {arrivals.map((arrival) => (
                  <div
                    key={arrival.id}
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                    data-testid={`arrival-${arrival.id}`}
                  >
                    <div>
                      <p className="font-medium text-slate-900">{arrival.guest?.name}</p>
                      <p className="text-sm text-slate-600">
                        Room {arrival.unit_id || 'TBA'} • {arrival.adults} guest(s)
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium text-slate-900">${arrival.base_amount}</p>
                      <p className="text-xs text-slate-500">{arrival.source}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card data-testid="departures-list">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                <Calendar size={18} className="text-blue-600" />
              </div>
              Today's Departures
            </CardTitle>
          </CardHeader>
          <CardContent>
            {departures.length === 0 ? (
              <p className="text-slate-500 text-center py-8">No departures today</p>
            ) : (
              <div className="space-y-3">
                {departures.map((departure) => (
                  <div
                    key={departure.id}
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                    data-testid={`departure-${departure.id}`}
                  >
                    <div>
                      <p className="font-medium text-slate-900">{departure.guest?.name}</p>
                      <p className="text-sm text-slate-600">
                        Room {departure.unit_id || 'TBA'} • Check-out
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium text-slate-900">${departure.base_amount}</p>
                      <p className="text-xs text-slate-500">{departure.source}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;
